/**
 * Browser shim for `node:crypto`, aliased in vite.config.ts.
 *
 * The frozen `@fume/core` was written Node-first and pulls three primitives
 * from `node:crypto`:
 *   - `hkdfSync`   (hkdf.ts)         — ON the runtime key-derivation path
 *   - `createHmac` (pake.ts)         — SPAKE2 confirmation tags
 *   - `createHash` (key-schedule.ts) — transcript hashing
 *
 * We back all three with `@noble/hashes` — an audited, synchronous, pure-JS
 * implementation whose output is byte-identical to Node's OpenSSL-backed
 * versions (verified against `crypto.hkdfSync` / `createHmac` / `createHash`).
 * This lets the frozen core run unmodified in the browser.
 */
import { hkdf } from '@noble/hashes/hkdf';
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';

type BytesLike = Uint8Array | ArrayBuffer | string;

function u8(data: BytesLike): Uint8Array {
  if (typeof data === 'string') return new TextEncoder().encode(data);
  if (data instanceof Uint8Array) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  return new Uint8Array(data);
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/** RFC 5869 HKDF-SHA256; matches Node's `crypto.hkdfSync('sha256', ...)`. */
export function hkdfSync(
  digest: string,
  ikm: BytesLike,
  salt: BytesLike,
  info: BytesLike,
  keylen: number,
): ArrayBuffer {
  if (digest !== 'sha256') {
    throw new Error(`node:crypto shim: unsupported hkdf digest ${digest}`);
  }
  const okm = hkdf(sha256, u8(ikm), u8(salt), u8(info), keylen);
  return okm.buffer.slice(okm.byteOffset, okm.byteOffset + okm.byteLength) as ArrayBuffer;
}

class HmacShim {
  private readonly h;
  constructor(algo: string, key: BytesLike) {
    if (algo !== 'sha256') throw new Error(`node:crypto shim: unsupported hmac ${algo}`);
    this.h = hmac.create(sha256, u8(key));
  }
  update(data: BytesLike): this {
    this.h.update(u8(data));
    return this;
  }
  digest(encoding?: 'hex'): Uint8Array | string {
    const out = this.h.digest();
    return encoding === 'hex' ? toHex(out) : out;
  }
}

class HashShim {
  private readonly h;
  constructor(algo: string) {
    if (algo !== 'sha256') throw new Error(`node:crypto shim: unsupported hash ${algo}`);
    this.h = sha256.create();
  }
  update(data: BytesLike): this {
    this.h.update(u8(data));
    return this;
  }
  digest(encoding?: 'hex'): Uint8Array | string {
    const out = this.h.digest();
    return encoding === 'hex' ? toHex(out) : out;
  }
}

export function createHmac(algo: string, key: BytesLike): HmacShim {
  return new HmacShim(algo, key);
}

export function createHash(algo: string): HashShim {
  return new HashShim(algo);
}

export default { hkdfSync, createHmac, createHash };
